// Deep Work Streak — local-first daily consistency tracker.
//
// Philosophy (aligned with DISTILL's "No BS, no shame"):
//  - Counts UNIQUE local calendar days on which the user opened the feed.
//  - Missing a day resets `current` to the fresh visit's day (no shame, no notifications).
//  - `best` preserves the personal-best streak across resets so past effort stays visible.
//  - History (last 30 days) drives the mini heatmap in the popover.
//  - Everything lives in localStorage — no server, no auth needed. If cleared, it resets.
//
// Data shape (localStorage key STREAK_KEY):
// {
//   current: number,        // current consecutive-day streak
//   best: number,           // personal best
//   last_visit_date: "YYYY-MM-DD" | null,  // most recent visit
//   history: ["YYYY-MM-DD", ...]           // most recent 30 entries, oldest→newest
// }

export const STREAK_KEY = "distill_streak_v1";
const HISTORY_MAX = 30;

/** Local-time YYYY-MM-DD (no timezone conversion — a "day" is the user's local day). */
function localDateISO(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daysBetween(a, b) {
  // a, b are "YYYY-MM-DD"; returns integer days (b - a).
  const da = new Date(a + "T00:00:00");
  const db = new Date(b + "T00:00:00");
  return Math.round((db - da) / 86400000);
}

const EMPTY = { current: 0, best: 0, last_visit_date: null, history: [] };

export function readStreak() {
  try {
    const raw = localStorage.getItem(STREAK_KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw);
    return {
      current: Number.isFinite(parsed.current) ? parsed.current : 0,
      best: Number.isFinite(parsed.best) ? parsed.best : 0,
      last_visit_date: parsed.last_visit_date || null,
      history: Array.isArray(parsed.history) ? parsed.history.slice(-HISTORY_MAX) : [],
    };
  } catch {
    return { ...EMPTY };
  }
}

function writeStreak(s) {
  try {
    localStorage.setItem(STREAK_KEY, JSON.stringify(s));
  } catch (err) {
    // Storage full / disabled — the streak is a nicety, not critical. Log for visibility.
    console.warn("[streak] Could not persist streak state:", err?.message || err);
  }
  return s;
}

/**
 * Record today as a visited day. Idempotent per calendar day.
 * Returns the new streak state.
 */
export function tickStreak(now = new Date()) {
  const today = localDateISO(now);
  const s = readStreak();

  if (s.last_visit_date === today) {
    // Already recorded today. No-op.
    return s;
  }

  let nextCurrent;
  if (!s.last_visit_date) {
    nextCurrent = 1; // very first visit ever
  } else {
    const gap = daysBetween(s.last_visit_date, today);
    if (gap === 1) {
      nextCurrent = s.current + 1; // consecutive day
    } else {
      nextCurrent = 1; // missed one or more days — quiet reset
    }
  }

  const nextBest = Math.max(s.best || 0, nextCurrent);
  const nextHistory = [...s.history, today].slice(-HISTORY_MAX);

  return writeStreak({
    current: nextCurrent,
    best: nextBest,
    last_visit_date: today,
    history: nextHistory,
  });
}

/**
 * Build a boolean array (length `days`, oldest→newest) indicating which of the last
 * `days` calendar days the user visited. Used by the popover heatmap strip.
 */
export function buildRecentActivity(days = 14, now = new Date()) {
  const s = readStreak();
  const set = new Set(s.history);
  const result = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const iso = localDateISO(d);
    result.push({ date: iso, active: set.has(iso) });
  }
  return result;
}

/** Reset (for future settings option). */
export function resetStreak() {
  try { localStorage.removeItem(STREAK_KEY); }
  catch (err) { console.warn("[streak] Could not clear streak state:", err?.message || err); }
  return { ...EMPTY };
}

// Exposed for tests
export const _internal = { localDateISO, daysBetween };
